import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import type { ReduxState } from "@dashboard/types";
import { shouldIgnoreShortcut } from "@dashboard/utils/shouldIgnoreShortcut";

import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

type WorkspaceSwitcherContextValue = {
  openSwitcher: (anchorEl?: HTMLElement | null) => void;
  open: boolean;
  isAdmin: boolean;
};

const WorkspaceSwitcherContext =
  React.createContext<WorkspaceSwitcherContextValue>({
    openSwitcher: () => undefined,
    open: false,
    isAdmin: false,
  });

export function useWorkspaceSwitcher(): WorkspaceSwitcherContextValue {
  return useContext(WorkspaceSwitcherContext);
}

const AdminWorkspaceSwitcherProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Cmd/Ctrl+K opens the switcher from anywhere under this provider. Captured
  // before it bubbles so bare-`k` list navigation (e.g. the tools page) never
  // sees it, and suppressed entirely while focus is in a text field. No anchor
  // → centered Spotlight presentation.
  useEffect(() => {
    function openOnHotkey(event: KeyboardEvent) {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === "k" &&
        !shouldIgnoreShortcut()
      ) {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(null);
        setOpen(true);
      }
    }
    document.addEventListener("keydown", openOnHotkey, true);
    return () => document.removeEventListener("keydown", openOnHotkey, true);
  }, []);

  const value = useMemo<WorkspaceSwitcherContextValue>(
    () => ({
      openSwitcher: (nextAnchorEl?: HTMLElement | null) => {
        setAnchorEl(nextAnchorEl ?? null);
        setOpen(true);
      },
      open,
      isAdmin: true,
    }),
    [open]
  );

  return (
    <WorkspaceSwitcherContext.Provider value={value}>
      {children}
      {/* Mount lazily so the all-restaurants query fires on first open, not on
          every page load. */}
      {open && (
        <WorkspaceSwitcher
          open
          anchorEl={anchorEl}
          onClose={() => setOpen(false)}
        />
      )}
    </WorkspaceSwitcherContext.Provider>
  );
};

export const WorkspaceSwitcherProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );

  if (!userIsAdmin) {
    return <>{children}</>;
  }

  return (
    <AdminWorkspaceSwitcherProvider>{children}</AdminWorkspaceSwitcherProvider>
  );
};
